import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { useDeviceBreakpoints } from '../../../hooks/useDeviceBreakpoints';
import { toCurrency } from '../../../utils/formatting';

export interface IChartHeaderProps extends React.PropsWithChildren {
  title: string;
  subtitle?: string;
  value?: string | number;
  className?: string;
}

export const ChartHeader = ({ title, subtitle, value, className = '', children }: IChartHeaderProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { isMobile } = useDeviceBreakpoints();

  // Format values for display
  const formattedValue = value !== undefined ? toCurrency(value) : undefined;

  return (
    <div ref={containerRef} className={cx('bg-secondary border-tertiary flex flex-col gap-5 border-b', className)}>
      <div className="flex flex-col gap-5 p-5">
        {/* Mobile Layout */}
        {isMobile && (
          <div className="flex flex-col gap-3">
            {/* Row 1: Title (left) and Children (right) */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-1">
                <div className="text-primary text-lg font-semibold">{title}</div>
                {subtitle && <span className="text-tertiary text-xs font-medium">{subtitle}</span>}
              </div>
              {children}
            </div>

            {/* Row 2: Large Value (left) */}
            {formattedValue && (
              <div className="text-primary text-[32px] font-semibold leading-[38px]">{formattedValue}</div>
            )}
          </div>
        )}

        {/* Desktop Layout */}
        {!isMobile && (
          <div className="flex w-full items-center justify-between gap-4">
            {/* Title */}
            <div className="flex flex-col gap-1">
              <div className="text-primary text-lg font-semibold">{title}</div>
              {subtitle && <span className="text-tertiary text-xs font-medium">{subtitle}</span>}
            </div>

            {/* Right side: value and children */}
            <div className="flex items-center gap-2">
              {formattedValue && <div className="text-primary text-xl font-semibold">{formattedValue}</div>}
              {children}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
